import { useEffect, useState } from "react";
import { Button, CssBaseline, Typography, Box, Container, Divider, Link as MUILink, Card, CardContent, Grid, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AES, enc } from "crypto-js";

import googleicon from "../assets/images/google.svg";
import { LoginSchema } from "../utils/SchemaValidation";
import Notification from "../utils/Notification";
import { FormProvider, RHFTextField } from "../components/hook-form";
import { BaseAPI } from "../utils/Api";
import Page from "../components/Controls/Page";
import { setItem } from "../utils/Helper";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (location.search !== "") {
      const getInfoFromToken = AES.decrypt(location.search.split("&")[1].split("=")[1], process.env.REACT_APP_SECRET).toString(enc.Utf8);
      const formatInfo = JSON.parse(getInfoFromToken);

      setItem(localStorage, "at", formatInfo.token);
      setItem(localStorage, "fn", formatInfo.f_name);
      setItem(localStorage, "p", formatInfo.phone);
      setItem(localStorage, "un", formatInfo.id.toString());

      navigate("/");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultValues = {
    email: "",
    password: "",
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    const body = {
      email: data.email,
      password: data.password,
    };
    await BaseAPI.post("/auth/signin", body)
      .then((res) => {
        setItem(localStorage, "e", res.data.data.email);
        if (res.data.data.is_verified) {
          setItem(localStorage, "at", res.data.data.token);
          setItem(localStorage, "fn", res.data.data.f_name);
          setItem(localStorage, "p", res.data.data.phone);
          setItem(localStorage, "un", res.data.data.id.toString());

          if (state) {
            navigate(state);
          } else {
            navigate("/");
          }
        } else {
          navigate("/verify-otp", { state: state });
          // <Navigate to='/verify-otp' state={state} replace={true} />;
        }
      })
      .catch((err) => {
        Notification(err?.response?.data?.message, "error");
      })
      .finally(() => {
        reset();
      });
  };

  return (
    <Page title='Login'>
      <Container component='main' maxWidth='sm' sx={{ height: "90vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CssBaseline />
        <Card>
          <CardContent>
            <Button
              size='large'
              fullWidth
              color='primary'
              sx={{ bgcolor: "#E3F1FF" }}
              startIcon={<img src={googleicon} alt='google' height='20' width='20' />}
              component={MUILink}
              href={`${process.env.REACT_APP_data_lagbe}/auth/google`}>
              Sign In with Google
            </Button>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Box my={4}>
                <Divider component='hr'></Divider>
              </Box>
              <Box display='flex' justifyContent='center'>
                <Typography variant='subtitle1'>Sign In With Email Address</Typography>
              </Box>
              <RHFTextField name='email' label='Email' />
              <RHFTextField
                name='password'
                label='Password'
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <Visibility /> : <VisibilityOff />}</IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type='submit' fullWidth variant='contained' disableElevation color='primary' disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={24} color='primary' /> : "Sign In"}
              </Button>
              <Grid container mt={2}>
                <Grid item xs>
                  <Button variant='text' color='primary' component={Link} to='/forgot-password'>
                    Forgot password?
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant='text' color='primary' component={Link} to='/register'>
                    Don't have an account? Sign Up
                  </Button>
                </Grid>
              </Grid>
            </FormProvider>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default Login;
